import { useContext } from 'react'

import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Col, Grid, Row } from '~/components/atoms/Grid'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import PortfolioBgLines from '~/components/atoms/PortfolioBgLines/PortfolioBgLines'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { modalTypes } from '~/components/modalLoader'
import { ReportTheme } from '~/components/report/types'
import { ComponentTheme } from '~/components/themes'
import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { GlobalStoreProps } from '~/contexts/GlobalStoreReducer'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { AUTH_CONFIG } from '~/pages/api/auth/auth-config'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import analyticsPusher, { analyticsEventTypes } from '~/utils/analyticsPusher'
import { getLocalStorage } from '~/utils/webStorage'

import styles from './MyPortfolioSalesHero.module.css'

export enum componentType {
  vertical = 'vertical',
  horizontal = 'horizontal',
}

export interface MyPortfolioSalesHeroProps {
  summary: string
  desktopImage: ImageProps
  mobileImage: ImageProps
  moduleId?: string
  negativeMarginTop?: boolean
  testID?: string
  postText?: string
}

export default function MyPortfolioSalesHero({
  summary,
  desktopImage,
  mobileImage,
  moduleId,
  negativeMarginTop = false,
  postText,
  testID,
}: MyPortfolioSalesHeroProps) {
  const t = useTranslations('auth')
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const { stateDispatch } = useContext(GlobalStoreContext)
  const { getModalDialogFlag } = useFeatureFlag()
  const modalDialogFlag = getModalDialogFlag()

  const openAuthModal = (defaultTab: number) => {
    analyticsPusher(analyticsEventTypes.gaEvent, {
      gaEventName: 'Auth form opened',
    })

    const returningUserData = getLocalStorage(AUTH_CONFIG.RETURNING_USER)
    const isReturningUser = returningUserData ? true : false

    let modalType = modalTypes.authModal

    if (defaultTab === 1) {
      modalType = !isReturningUser
        ? modalTypes.authSignupDialog
        : modalTypes.authModal
    }

    stateDispatch({
      type: GlobalStoreProps.UPDATE_MODAL_STATE,
      payload: {
        isOpen: true,
        component: modalType,
        content: null,
        isDrawer: modalDialogFlag !== 'control',
        props: {
          isReducedWidth: true,
          defaultTab: defaultTab,
          showMpModalHeader: true,
          ignoreCookie: false,
          theme: ReportTheme.primary,
          drawerTheme: ReportTheme.primary,
        },
      },
    })
  }

  return (
    <div
      data-testid={testID}
      className={classNames({
        [styles.negativeMarginTop]: negativeMarginTop,
      })}
    >
      <Fixture
        id={moduleId}
        theme={ComponentTheme.primary}
        verticalPadding={true}
        noPaddingBottom
        className={styles.base}
      >
        <div className={styles.background} />
        <PortfolioBgLines />

        <Container gutters={gutterTypes.always} cssClass={styles.container}>
          <Grid>
            <Row>
              <Col variant="col-1/2--md">
                <div className={styles.content}>
                  <Text
                    Tag="h1"
                    size={TextSizes.primaryTitleXLrg}
                    className={styles.title}
                  >
                    {t.rich('myPortfolio', {
                      em: chunks => <em>{chunks}</em>,
                    })}
                  </Text>
                  <Text
                    size={TextSizes.secondaryTitleLrg}
                    Tag="p"
                    className={styles.body}
                  >
                    {summary}
                  </Text>
                  <div className={styles.buttons}>
                    <Button
                      button={{
                        type: ButtonTypes.button,
                        onClick: () => openAuthModal(0),
                      }}
                      variant={ButtonVariants.primaryLight}
                    >
                      {t('login')}
                    </Button>
                    <Button
                      button={{
                        type: ButtonTypes.button,
                        onClick: () => openAuthModal(1),
                      }}
                      variant={ButtonVariants.secondaryLight}
                    >
                      {t('signUp')}
                    </Button>
                  </div>
                  {postText && (
                    <Text size={TextSizes.bodyMed} className={styles.postText}>
                      {postText}
                    </Text>
                  )}
                </div>
              </Col>
              <Col variant="col-1/2--md" cssClass={styles.mediaContainer}>
                <div className={styles.media}>
                  {isMobile ? (
                    <Image
                      {...mobileImage}
                      alt="MyPortfolio - MyArtBroker"
                      objectFit="initial"
                    />
                  ) : (
                    <Image
                      {...desktopImage}
                      alt="MyPortfolio - MyArtBroker"
                      objectFit="cover"
                    />
                  )}
                </div>
              </Col>
            </Row>
          </Grid>
        </Container>
      </Fixture>
    </div>
  )
}
